/**
 * Where a tour's instruction box goes beside its target (TOADO-971): below
 * it when there is room, above it otherwise, and on a narrow screen docked
 * to the bottom, where it never covers the control being pointed at more
 * than it has to. Always wholly on screen, with a gutter.
 */
export interface Rect { top: number; left: number; width: number; height: number }
export interface Size { width: number; height: number }

export type Placement = { side: 'below' | 'above' | 'right' | 'left'; top: number; left: number } | { side: 'docked' };

export const GUTTER = 16;
/** Space between the target and the box. */
export const GAP = 12;
/** Narrower than this, the box is docked rather than placed beside its target. */
export const NARROW = 600;

export function placeBox(target: Rect, box: Size, viewport: Size): Placement {
  if (viewport.width < NARROW) return { side: 'docked' };
  const below = target.top + target.height + GAP;
  const above = target.top - GAP - box.height;
  const fitsBelow = below + box.height <= viewport.height - GUTTER;
  const fitsAbove = above >= GUTTER;
  // A target too tall for either, a dialog or a settings section: beside it,
  // right then left, where there is room, rather than over its foot (Jason, 28 Oct).
  if (!fitsBelow && !fitsAbove) {
    const top = Math.max(GUTTER, Math.min(target.top, viewport.height - GUTTER - box.height));
    const right = target.left + target.width + GAP;
    if (right + box.width <= viewport.width - GUTTER) return { side: 'right', top, left: right };
    const left = target.left - GAP - box.width;
    if (left >= GUTTER) return { side: 'left', top, left };
  }
  // Below when it fits there, or when neither side does and below has more room.
  const side = fitsBelow || (!fitsAbove && viewport.height - below >= target.top) ? 'below' : 'above';
  const rawTop = side === 'below' ? below : above;
  const top = Math.max(GUTTER, Math.min(rawTop, viewport.height - GUTTER - box.height));
  // Lined up with the target's left edge, kept on screen.
  const left = Math.max(GUTTER, Math.min(target.left, viewport.width - GUTTER - box.width));
  return { side, top, left };
}
